import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import {
  getSoftNavSectionIds,
  swapSoftNavSections,
  swapSoftNavPage,
  beginSoftNav,
  endSoftNav,
} from '@theme/section-swap';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { bindCardVariationHover } from '@theme/card-variation-hover';

/**
 * Manages prefetching and caching of product sections using the Section Rendering API.
 * Implements stale-while-revalidate: serves cached data immediately and refreshes in background.
 * Singleton instance shared across all ProductVariations components.
 */
class VariationPrefetchManager {
  /** @type {Map<string, { promise: Promise<Record<string, string>>, timestamp: number }>} */
  #cache = new Map();

  /** @type {number} */
  #maxCacheSize = 20;

  /** @type {number} Cache TTL in milliseconds (60 seconds). */
  #ttl = 60_000;

  /**
   * Prefetches product sections using Section Rendering API.
   * Returns cached data immediately if fresh; triggers background revalidation if stale.
   * @param {string} url - The product URL to prefetch.
   * @returns {Promise<Record<string, string>> | undefined} Promise resolving to section HTML map.
   */
  prefetch(url) {
    if (!url) return undefined;

    const cached = this.#cache.get(url);
    const now = Date.now();

    if (cached) {
      if (now - cached.timestamp < this.#ttl) return cached.promise;
      this.#fetchAndCache(url); // background revalidation, no abort signal
      return cached.promise;
    }

    return this.#fetchAndCache(url);
  }

  /**
   * Gets cached sections or fetches them. Accepts an AbortSignal for cancellation.
   * Honors the same TTL as prefetch() — a stale entry is refetched (with the caller's own
   * signal, since this is the real click/popstate path, not a background revalidation) rather
   * than served indefinitely until LRU eviction.
   * @param {string} url - The product URL.
   * @param {AbortSignal} [signal] - Optional signal to cancel the fetch.
   * @returns {Promise<Record<string, string>>} Promise resolving to section HTML map.
   */
  async get(url, signal) {
    const cached = this.#cache.get(url);
    if (cached && Date.now() - cached.timestamp < this.#ttl) return cached.promise;

    return this.#fetchAndCache(url, signal);
  }

  /**
   * Fetches and stores sections in cache.
   * @param {string} url - The product URL.
   * @param {AbortSignal} [signal] - Optional abort signal.
   * @returns {Promise<Record<string, string>>} Promise resolving to section HTML map.
   */
  #fetchAndCache(url, signal) {
    const sectionUrl = this.#buildSectionUrl(url);

    const fetchPromise = fetch(sectionUrl, {
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        this.#cache.delete(url); // remove failed/aborted entries so they can be retried
        throw error;
      });

    this.#cache.set(url, { promise: fetchPromise, timestamp: Date.now() });
    this.#trimCache();

    return fetchPromise;
  }

  /**
   * Builds Section Rendering API URL. Forwards any `variant` query param
   * through so the server renders the target page with that variant
   * selected.
   *
   * @param {string} url - The product URL.
   * @returns {string} The section rendering URL.
   */
  #buildSectionUrl(url) {
    const [baseUrl, query] = url.split('?');
    const sections = getSoftNavSectionIds().join(',');
    const params = new URLSearchParams(query || '');
    const variantId = params.get('variant');
    return variantId
      ? `${baseUrl}?sections=${sections}&variant=${encodeURIComponent(variantId)}`
      : `${baseUrl}?sections=${sections}`;
  }

  /**
   * Checks if a URL has been prefetched.
   * @param {string} url - The URL to check.
   * @returns {boolean} True if prefetched.
   */
  has(url) {
    return this.#cache.has(url);
  }

  /**
   * Trims the cache to prevent memory issues.
   */
  #trimCache() {
    if (this.#cache.size <= this.#maxCacheSize) return;

    const keysToDelete = Array.from(this.#cache.keys()).slice(0, this.#cache.size - this.#maxCacheSize);
    for (const key of keysToDelete) {
      this.#cache.delete(key);
    }
  }
}

const prefetchManager = new VariationPrefetchManager();

/**
 * Connected standalone `product-variations` instances that have claimed popstate
 * ownership, in connection order. Only the first one (the "leader") actually acts
 * on a given popstate event — a page can render more than one standalone instance
 * (e.g. a product-variations block plus a gear-set-variations drawer block on the
 * same PDP), and without a single leader each instance would independently
 * fetch/swap the same sections for the same popstate event.
 * @type {ProductVariations[]}
 */
const popstateOwners = [];

/**
 * Product variations component - handles thumbnails with navigation or checkbox selection
 *
 * @extends {Component}
 */
export class ProductVariations extends Component {
  /** @type {string} */
  #selectionMode = 'navigate';

  /** @type {HTMLInputElement[]} */
  #checkboxes = [];

  /** @type {boolean} */
  #isLoading = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #loadingTimer = null;

  /** @type {AbortController | null} */
  #currentFetchController = null;

  /** @type {Map<HTMLImageElement, { src: string, srcset: string, alt: string }>} Original src/srcset/alt per previewed image, for restore. */
  #previewedImages = new Map();

  /** @type {Map<HTMLVideoElement, { src: string | null, synthetic: boolean, counterpartImage: HTMLImageElement | null }>} Original state per previewed video, for restore. */
  #previewedVideos = new Map();

  /** @type {Map<Element, HTMLImageElement>} Synthesized `<img>` per video-only slide (lazy, reused). */
  #syntheticSlideImages = new Map();

  /** @type {Map<Element, HTMLVideoElement>} Synthesized `<video>` per image-only slide (lazy, reused). */
  #syntheticSlideVideos = new Map();

  /** True once a click has begun soft-navigating, so a trailing mouseleave doesn't restore the preview mid-navigation. */
  #navigatingAway = false;

  /** @type {(() => void) | null} Cleanup for the card-context hover/select binding (see connectedCallback). */
  #unbindCardHover = null;

  connectedCallback() {
    super.connectedCallback();

    this.swatchesContainer = this.querySelector('.product-card__swatches');
    this.#selectionMode = this.dataset.selectionMode || 'navigate';

    if (!this.swatchesContainer) {
      return;
    }

    if (this.#selectionMode === 'checkbox') {
      this.#initializeCheckboxMode();
      return;
    }

    // Soft-nav below only applies standalone on the PDP; inside a product-card, use card_hover's binding instead.
    const productCard = this.closest('product-card') || this.closest('.resource-card');
    if (productCard) {
      this.#unbindCardHover = bindCardVariationHover(this, productCard);
      return;
    }

    this.#initializeNavigateMode();
    this.#initializePopstateHandler();
  }

  /** @type {((event: PopStateEvent) => void) | null} */
  #popstateHandler = null;

  /**
   * Initialize popstate handler for browser back/forward navigation.
   */
  #initializePopstateHandler() {
    this.#popstateHandler = this.#handlePopstate.bind(this);
    if (this.#popstateHandler) {
      window.addEventListener('popstate', this.#popstateHandler);
      // Claim ownership of popstate-driven section swaps so soft-navigation.js's
      // own popstate listener (same main+recommendations refetch, less complete —
      // see its early-return check) doesn't also fire and double the work. A
      // reference count, not a boolean: a page can render more than one
      // standalone instance (e.g. a product-variations block plus a
      // gear-set-variations drawer block on the same PDP), and one
      // disconnecting must not un-claim ownership while another is still
      // mounted.
      const w = /** @type {any} */ (window);
      w.__productVariationsPopstateOwner = (w.__productVariationsPopstateOwner || 0) + 1;
      // Also register as a leadership candidate — see popstateOwners above.
      popstateOwners.push(this);
    }
  }

  /**
   * Handle browser back/forward navigation.
   * Updates DOM only — does NOT push a new history state.
   * @param {PopStateEvent} event - The popstate event.
   */
  async #handlePopstate(event) {
    // Only the leader instance acts; other connected instances defer to it so the
    // same popstate event doesn't trigger a redundant fetch/swap per instance.
    if (popstateOwners[0] !== this) return;

    if (event.state?.softNav || document.location.pathname.includes('/products/')) {
      // Shared with soft-navigation.js — starting a nav here aborts any
      // length/gear-set swap in flight, and vice versa, so the two never race.
      const thisController = beginSoftNav();
      this.#currentFetchController = thisController;
      const scrollPosition = window.scrollY;
      try {
        // The history entry may come from a gear-set or length switch, which
        // refreshed the whole template area - do the same, so every section
        // shows the product of the restored URL.
        await this.#updatePageContent(document.location.href, thisController.signal, { wholePage: true });
        window.scrollTo(0, scrollPosition);
      } catch (error) {
        // #updatePageContent rethrows AbortError when superseded by a newer navigation — don't
        // restore scroll for a call whose content was never actually applied.
        if (/** @type {Error} */ (error).name !== 'AbortError') throw error;
      } finally {
        endSoftNav(thisController);
        // Only clear if a newer call hasn't already replaced it — otherwise this
        // (now-superseded) call's cleanup would wipe out the active one.
        if (this.#currentFetchController === thisController) this.#currentFetchController = null;
      }
    }
  }

  /**
   * Initialize navigate mode: prefetches section HTML and preloads images on hover,
   * then intercepts clicks for soft navigation.
   */
  #initializeNavigateMode() {
    if (!this.swatchesContainer) return;

    const thumbnails = this.swatchesContainer.querySelectorAll('.product-card__thumbnail');
    if (thumbnails.length === 0) return;

    thumbnails.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      // Warm the sibling product once (section HTML + its main image) so a
      // click soft-navigates instantly. mouseenter = desktop hover;
      // pointerdown = touch (fires before click).
      let warmed = false;
      const warm = () => {
        if (warmed) return;
        warmed = true;
        const url = thumbnail.dataset.variationUrl;
        if (url) prefetchManager.prefetch(url)?.catch(() => {});
        const image = thumbnail.dataset.variationImage;
        if (image) {
          const srcset = thumbnail.dataset.variationImageSrcset || undefined;
          // Prefer the live gallery image's `sizes` so the preload picks the
          // exact srcset candidate the main image will actually render.
          const galleryImg = this.closest('[id^="shopify-section"]')?.querySelector(
            'media-gallery img.product-media__image',
          );
          const rawSizes = galleryImg?.sizes || thumbnail.dataset.variationImageSizes;
          const sizes = rawSizes?.replace(/^\s*auto\s*,\s*/i, '') || undefined;
          preloadImage(image, { srcset, sizes, fetchpriority: true });
        }

        // Second visible slide in a two-up carousel (Columns: Two) previews
        // the colourway's secondary photo, if it has one.
        const secondaryImage = thumbnail.dataset.variationHoverSecondaryImage;
        if (secondaryImage) {
          const secondarySrcset = thumbnail.dataset.variationHoverSecondarySrcset || undefined;
          preloadImage(secondaryImage, { srcset: secondarySrcset, fetchpriority: true });
        }
      };

      // Desktop: preview this colourway in the main gallery image while hovering.
      thumbnail.addEventListener('mouseenter', () => {
        warm();
        this.#previewVariationImage(thumbnail);
      });
      thumbnail.addEventListener('mouseleave', () => this.#restoreVariationImage());

      // Touch: no hover — just warm the sibling on first touch.
      thumbnail.addEventListener('pointerdown', warm);

      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Keep the previewed image visible through the soft-nav; the section
        // morph then replaces it with the real gallery for the new colourway.
        this.#navigatingAway = true;
        this.#handleVariationClick(thumbnail);
      });
    });

    const navigateButtons = this.querySelectorAll('[data-variation-navigate-button]');
    navigateButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.#isLoading) return;
        const url = button.dataset.variationUrl;
        if (url && !button.hasAttribute('disabled')) this.#softNavigate(url);
      });
    });
  }

  /**
   * @typedef {Object} GallerySlideTarget
   * @property {Element} slide - The slide wrapper.
   * @property {HTMLImageElement | null} img
   * @property {HTMLVideoElement | null} video
   * @property {HTMLElement | null} deferredMedia - Closest `<deferred-media>` ancestor of `video`; its own `playMedia()`/`pauseMedia()` are used instead of driving `video` directly.
   */

  /**
   * The visible slide(s) right now (up to 2 in a two-up carousel), re-queried
   * on every hover.
   * @returns {GallerySlideTarget[]}
   */
  #getActiveGallerySlides() {
    const gallery = this.closest('[id^="shopify-section"]')?.querySelector('media-gallery');
    if (!gallery) return [];

    /** @param {string} selector */
    const collectSlideEls = (selector) => Array.from(gallery.querySelectorAll(selector));

    let slideEls = collectSlideEls('slideshow-slide[aria-hidden="false"]');
    if (!slideEls.length) slideEls = collectSlideEls('slideshow-slide:not([aria-hidden="true"])');

    if (!slideEls.length) {
      // Grid layout — no <slideshow-slide> wrapper.
      const img = gallery.querySelector('img.product-media__image');
      if (!(img instanceof HTMLImageElement)) return [];
      return [{ slide: img.closest('.product-media-container') ?? img, img, video: null, deferredMedia: null }];
    }

    return slideEls.map((slide) => {
      const img = slide.querySelector('img.product-media__image');
      const video = slide.querySelector('video.product-media__video-element');
      const deferredMedia = video?.closest('deferred-media') ?? null;
      return {
        slide,
        img: img instanceof HTMLImageElement ? img : null,
        video: video instanceof HTMLVideoElement ? video : null,
        deferredMedia: deferredMedia instanceof HTMLElement ? deferredMedia : null,
      };
    });
  }

  /** Lazily creates a hidden `<img>` next to a video-only slide's `<deferred-media>`, so an image preview has somewhere to render.
   * @param {GallerySlideTarget} target
   * @returns {HTMLImageElement | null}
   */
  #ensureSlideImage(target) {
    if (target.img) return target.img;
    if (!target.video) return null;

    const existing = this.#syntheticSlideImages.get(target.slide);
    if (existing) {
      target.img = existing;
      return existing;
    }

    const productMedia = target.video.closest('.product-media');
    const anchor = target.video.closest('deferred-media') ?? target.video;
    if (!productMedia) return null;

    const img = document.createElement('img');
    img.className = 'product-media__image';
    img.loading = 'lazy';
    img.alt = '';
    img.hidden = true;
    img.style.display = 'none';
    productMedia.insertBefore(img, anchor);

    this.#syntheticSlideImages.set(target.slide, img);
    target.img = img;
    return img;
  }

  /** Reverse of `#ensureSlideImage`: lazily creates a hidden, muted `<video>` next to a slide's own `<img>`, driven directly (no `<deferred-media>` controller).
   * @param {GallerySlideTarget} target
   * @returns {HTMLVideoElement | null}
   */
  #ensureSlideVideo(target) {
    if (target.video) return target.video;
    if (!target.img) return null;

    const existing = this.#syntheticSlideVideos.get(target.slide);
    if (existing) {
      target.video = existing;
      return existing;
    }

    const productMedia = target.img.closest('.product-media');
    if (!productMedia) return null;

    const video = document.createElement('video');
    video.className = 'product-media__video-element';
    video.muted = true;
    video.loop = true;
    video.setAttribute('playsinline', '');
    video.hidden = true;
    video.style.display = 'none';
    productMedia.insertBefore(video, target.img.nextSibling);

    this.#syntheticSlideVideos.set(target.slide, video);
    target.video = video;
    return video;
  }

  /** @param {Element | null} el */
  #showMedia(el) {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = false;
    el.style.display = '';
  }

  /** @param {Element | null} el */
  #hideMedia(el) {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = true;
    el.style.display = 'none';
  }

  /**
   * Point the visible gallery slide(s) at the hovered colourway's media —
   * primary image on slide 1, secondary image/video on slide 2 if it has one.
   * @param {HTMLElement} thumbnail
   */
  #previewVariationImage(thumbnail) {
    if (thumbnail.classList.contains('is-active')) return;

    const targets = this.#getActiveGallerySlides();
    if (!targets.length) return;

    const activeImages = targets.map((t) => t.img).filter((img) => img != null);
    const activeVideos = targets.map((t) => t.video).filter((video) => video != null);

    // Active slide(s) changed since the last preview.
    for (const [previewedImg, original] of this.#previewedImages) {
      if (activeImages.includes(previewedImg)) continue;
      previewedImg.src = original.src;
      previewedImg.srcset = original.srcset;
      previewedImg.alt = original.alt;
      this.#previewedImages.delete(previewedImg);
    }
    for (const [previewedVideo, original] of this.#previewedVideos) {
      if (activeVideos.includes(previewedVideo)) continue;
      this.#fullyRestoreVideo(previewedVideo, original);
    }

    const sources = [
      {
        image: thumbnail.dataset.variationImage,
        srcset: thumbnail.dataset.variationImageSrcset,
        video: undefined,
      },
      {
        image: thumbnail.dataset.variationHoverSecondaryImage,
        srcset: thumbnail.dataset.variationHoverSecondarySrcset,
        video: thumbnail.dataset.variationHoverSecondaryVideo,
      },
    ];

    targets.forEach((target, index) => {
      const source = sources[index];
      if (!source) return;

      if (source.video) {
        const video = this.#ensureSlideVideo(target);
        if (!video) return;

        this.#hideMedia(target.img);
        this.#trackVideo(video, !target.deferredMedia, target.img);
        this.#previewVideo(video, target.deferredMedia, source.video);
      } else if (source.image) {
        const img = this.#ensureSlideImage(target);
        if (!img) return;

        if (target.video) {
          this.#trackVideo(target.video, !target.deferredMedia, target.img);
          target.video.pause();
          this.#hideMedia(target.deferredMedia ?? target.video);
        }
        this.#showMedia(img);

        if (!this.#previewedImages.has(img)) {
          this.#previewedImages.set(img, { src: img.src, srcset: img.srcset, alt: img.alt });
        }
        img.src = source.image;
        img.srcset = source.srcset || '';
        img.alt = thumbnail.dataset.variationTitle || thumbnail.getAttribute('aria-label') || '';
      }
    });
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {boolean} synthetic
   * @param {HTMLImageElement | null} counterpartImage - Slide's own image to restore if this video is synthetic.
   */
  #trackVideo(video, synthetic, counterpartImage) {
    if (!this.#previewedVideos.has(video)) {
      this.#previewedVideos.set(video, { src: video.getAttribute('src'), synthetic, counterpartImage });
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement | null} deferredMedia
   * @param {string} url
   */
  #previewVideo(video, deferredMedia, url) {
    this.#showMedia(deferredMedia ?? video);

    if (video.getAttribute('src') !== url) {
      video.pause();
      video.setAttribute('src', url);
      video.load();
    }

    if (deferredMedia && typeof (/** @type {any} */ (deferredMedia).playMedia) === 'function') {
      /** @type {any} */ (deferredMedia).playMedia();
    } else {
      video.play().catch(() => {});
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {{ src: string | null, synthetic: boolean, counterpartImage: HTMLImageElement | null }} original
   */
  #fullyRestoreVideo(video, original) {
    video.pause();

    if (original.synthetic) {
      this.#hideMedia(video);
      this.#showMedia(original.counterpartImage);
      this.#previewedVideos.delete(video);
      return;
    }

    if (original.src) {
      video.setAttribute('src', original.src);
    } else {
      // Originally played from <source> children — drop the override to re-enable them.
      video.removeAttribute('src');
    }
    video.load();

    const deferredMedia = video.closest('deferred-media');
    const image = this.#syntheticSlideImages.get(video.closest('slideshow-slide') ?? video) ?? null;

    this.#hideMedia(image);
    this.#showMedia(deferredMedia);

    if (deferredMedia && typeof (/** @type {any} */ (deferredMedia).playMedia) === 'function') {
      /** @type {any} */ (deferredMedia).playMedia();
    }

    this.#previewedVideos.delete(video);
  }

  /** Restore every previewed gallery slide to its own product's media. */
  #restoreVariationImage() {
    // After a click we're soft-navigating to the sibling product; keep the
    // preview so the gallery doesn't flash back before the section morphs in.
    if (this.#navigatingAway) return;

    for (const [img, original] of this.#previewedImages) {
      img.src = original.src;
      img.srcset = original.srcset;
      img.alt = original.alt;
    }
    this.#previewedImages.clear();

    for (const [video, original] of this.#previewedVideos) {
      this.#fullyRestoreVideo(video, original);
    }
  }

  /**
   * Initialize checkbox mode
   */
  #initializeCheckboxMode() {
    // Support both old checkbox style and new button style
    this.#checkboxes = Array.from(this.querySelectorAll('.product-card__variation-checkbox'));
    const buttons = Array.from(this.querySelectorAll('[data-variation-button]'));

    if (this.#checkboxes.length === 0 && buttons.length === 0) {
      return;
    }

    // Handle old checkbox style
    this.#checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.#handleCheckboxChange());
    });

    // Handle new button style
    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        if (e instanceof MouseEvent) {
          this.#handleButtonClick(e);
        }
      });
    });

    this.#updateSummary();
  }

  /**
   * Handle checkbox change
   */
  #handleCheckboxChange() {
    this.#updateSummary();
  }

  /**
   * Handle button click for add/remove variations
   * @param {MouseEvent} event - The click event
   */
  #handleButtonClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const card = button.closest('.product-variation-card');
    if (!card) return;

    // Toggle selection
    const isSelected = card.classList.contains('is-selected');

    if (isSelected) {
      card.classList.remove('is-selected');
      button.querySelector('.product-variation-card__button-text')?.replaceWith(
        Object.assign(document.createElement('span'), {
          className: 'product-variation-card__button-text',
          textContent: this.dataset.i18nAdd || 'Add',
        }),
      );
    } else {
      card.classList.add('is-selected');
      button.querySelector('.product-variation-card__button-text')?.replaceWith(
        Object.assign(document.createElement('span'), {
          className: 'product-variation-card__button-text',
          textContent: '✓ Added',
        }),
      );
    }

    this.#updateSummary();
  }

  /**
   * Update legend with selected product names and external trigger
   */
  #updateSummary() {
    // Get selected items from either checkboxes or cards
    const selectedCheckboxes = this.#checkboxes.filter((cb) => cb.checked);
    const selectedCards = Array.from(this.querySelectorAll('.product-variation-card.is-selected')).filter(
      (el) => el instanceof HTMLElement,
    );

    /** @type {Array<HTMLInputElement|HTMLElement>} */
    const selectedItems = selectedCheckboxes.length > 0 ? selectedCheckboxes : selectedCards;
    const count = selectedItems.length;

    // Update selected items in legend
    const selectedItemsElement = this.querySelector('.product-variations__selected-items');
    if (selectedItemsElement) {
      if (count === 0) {
        selectedItemsElement.textContent = '';
      } else {
        // Get all selected product names
        let names;
        if (selectedCheckboxes.length > 0) {
          names = selectedCheckboxes
            .map((cb) => {
              const wrapper = cb.closest('[data-variation-title]');
              return wrapper instanceof HTMLElement ? wrapper.dataset.variationTitle : null;
            })
            .filter(Boolean);
        } else {
          names = selectedCards
            .map((card) => {
              if (!(card instanceof HTMLElement)) return null;
              return card.dataset.variationTitle || null;
            })
            .filter(Boolean);
        }
        selectedItemsElement.textContent = names.length > 0 ? names.join(', ') : '';
      }
    }

    // Update external trigger button
    this.#updateTriggerButton(selectedItems);
  }

  /**
   * Update the trigger button outside the drawer with selected products
   * @param {Array<HTMLInputElement|HTMLElement>} selectedItems - Array of selected items (checkboxes or cards)
   */
  #updateTriggerButton(selectedItems) {
    // Find the drawer component and trigger button
    const drawer = this.closest('product-variations-drawer');
    if (!drawer) return;

    const triggerSelected = drawer.querySelector('[data-trigger-selected]');
    const triggerImage = drawer.querySelector('[data-trigger-image]');
    const triggerImageWrapper = drawer.querySelector('[data-trigger-image-wrapper]');

    if (!triggerSelected) return;

    if (selectedItems.length === 0) {
      triggerSelected.textContent = '';

      // Hide image when nothing is selected
      if (triggerImageWrapper instanceof HTMLElement) {
        triggerImageWrapper.style.display = 'none';
      }
      return;
    }

    // Get selected product names and images
    const products = selectedItems
      .map((item) => {
        let wrapper;
        if (item instanceof HTMLInputElement) {
          // Old checkbox style
          wrapper = item.closest('[data-variation-title]');
        } else if (item instanceof HTMLElement) {
          // New card style
          wrapper = item;
        }
        if (!(wrapper instanceof HTMLElement)) return null;
        return {
          title: wrapper.dataset.variationTitle || null,
          image: wrapper.dataset.variationImage || null,
        };
      })
      .filter((p) => p !== null && !!p.title);

    if (products.length === 0) return;

    const names = products.map((p) => p && p.title).filter(Boolean);

    // Check if all selected products have the same name
    const allSameName = names.every((name) => name === names[0]);

    // Format the text
    const firstName = names[0];
    if (!firstName) return;

    if (allSameName) {
      // All same product: "2 x cushion"
      triggerSelected.textContent = `${names.length} x ${firstName}`;
    } else {
      // Different products: show first name + count if multiple
      if (names.length === 1) {
        triggerSelected.textContent = firstName;
      } else {
        triggerSelected.textContent = `${firstName} +${names.length - 1}`;
      }
    }

    // Update image - show first product's image
    const firstProduct = products[0];
    if (
      triggerImage instanceof HTMLImageElement &&
      triggerImageWrapper instanceof HTMLElement &&
      firstProduct &&
      firstProduct.image
    ) {
      const imageUrl = firstProduct.image;
      // Generate appropriate image size URL (120x120 for display)
      const sizedImageUrl = imageUrl.includes('cdn.shopify.com')
        ? imageUrl.replace(/\.(jpg|jpeg|png|webp|gif)/i, '_120x120.$1')
        : imageUrl;

      triggerImage.src = sizedImageUrl;
      triggerImage.alt = firstName || '';
      triggerImageWrapper.style.display = 'block';
    } else if (triggerImageWrapper instanceof HTMLElement) {
      triggerImageWrapper.style.display = 'none';
    }
  }

  /**
   * Get selected variation IDs (public method for integration with add to cart)
   * @returns {string[]} Array of variant IDs
   */
  getSelectedVariations() {
    if (this.#selectionMode !== 'checkbox') {
      return [];
    }

    // Check for old checkbox style
    const selectedCheckboxes = this.#checkboxes.filter((cb) => cb.checked && !cb.disabled);
    if (selectedCheckboxes.length > 0) {
      return selectedCheckboxes.map((cb) => cb.value);
    }

    // Check for new card style
    const selectedCards = Array.from(this.querySelectorAll('.product-variation-card.is-selected:not(.is-unavailable)'));
    return selectedCards
      .map((card) => {
        if (!(card instanceof HTMLElement)) return null;
        return card.dataset.variationId || null;
      })
      .filter((id) => id !== null);
  }

  /**
   * Check if checkbox mode is active and has selections
   * @returns {boolean}
   */
  hasSelectedVariations() {
    return this.#selectionMode === 'checkbox' && this.getSelectedVariations().length > 0;
  }

  /**
   * Clear all selections
   */
  clearSelections() {
    // Clear old checkbox style
    this.#checkboxes.forEach((cb) => (cb.checked = false));

    // Clear new card style
    const selectedCards = this.querySelectorAll('.product-variation-card.is-selected');
    selectedCards.forEach((card) => {
      card.classList.remove('is-selected');
      const button = card.querySelector('[data-variation-button]');
      if (button) {
        const buttonText = button.querySelector('.product-variation-card__button-text');
        if (buttonText) {
          buttonText.textContent = this.dataset.i18nAdd || 'Add';
        }
      }
    });

    this.#updateSummary();
  }

  /**
   * Add selected variations to cart (called by product form)
   * @param {string|null} currentVariantId - The current product variant ID
   * @returns {Promise<Object>} Result object with success status and data
   */
  async addSelectedToCart(currentVariantId = null) {
    const selectedVariantIds = this.getSelectedVariations();
    /** @type {string[]} */
    const allVariantIds = [];

    if (currentVariantId) {
      allVariantIds.push(currentVariantId);
    }

    selectedVariantIds.forEach((id) => {
      if (!allVariantIds.includes(id)) {
        allVariantIds.push(id);
      }
    });

    if (allVariantIds.length === 0) {
      return {
        success: false,
        error: this.dataset.i18nNoProducts || 'No products to add',
      };
    }

    try {
      const items = allVariantIds.map((id) => ({
        id: parseInt(id, 10),
        quantity: 1,
      }));

      const config = fetchConfig('javascript');
      const headers = {
        ...config.headers,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ items }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = this.dataset.i18nCartError || 'Error adding items to cart';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorData.description || errorMessage;
        } catch {
          errorMessage =
            response.status === 422 ? this.dataset.i18nUnavailable || 'Some products are unavailable' : errorMessage;
        }

        return {
          success: false,
          error: errorMessage,
        };
      }

      const data = JSON.parse(responseText);

      if (data.items) {
        // @ts-ignore - data.items is from JSON response
        const addedItemsCount = data.items.reduce((sum, item) => sum + (item.quantity || 0), 0);

        this.dispatchEvent(
          new CartAddEvent({}, data.items[0].variant_id.toString(), {
            source: 'product-form-component',
            itemCount: addedItemsCount,
            productId: this.dataset.productId,
            sections: data.sections,
          }),
        );

        setTimeout(() => {
          this.clearSelections();
        }, 500);

        return {
          success: true,
          data: data,
          itemCount: data.items.length,
        };
      } else {
        return {
          success: false,
          error: this.dataset.i18nUnexpected || 'Unexpected server response',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || this.dataset.i18nCartError || 'Error adding items to cart',
      };
    }
  }

  /**
   * Handle variation thumbnail click
   * @param {HTMLElement} thumbnail - The clicked thumbnail
   */
  async #handleVariationClick(thumbnail) {
    if (thumbnail.classList.contains('is-active') || this.#isLoading) {
      return;
    }

    const variationUrl = thumbnail.dataset.variationUrl;

    if (!variationUrl) {
      return;
    }

    if (this.swatchesContainer) {
      this.swatchesContainer
        .querySelectorAll('.product-card__thumbnail')
        .forEach((t) => t.classList.remove('is-active'));
    }
    thumbnail.classList.add('is-active');

    if (!thumbnail.hasAttribute('onclick')) {
      await this.#softNavigate(variationUrl);
    }
  }

  /**
   * Navigates to a variation URL: updates DOM and pushes a new history state.
   * Cancels any in-flight navigation request before starting a new one.
   * @param {string} url - The product URL to navigate to.
   */
  async #softNavigate(url) {
    // Shared with soft-navigation.js — starting a nav here aborts any
    // length/gear-set swap in flight, and vice versa, so the two never race.
    const thisController = beginSoftNav();
    this.#currentFetchController = thisController;

    try {
      await this.#updatePageContent(url, thisController.signal);
      history.pushState({ softNav: true }, '', url);
    } catch (error) {
      // #updatePageContent rethrows AbortError when superseded by a newer navigation — don't
      // push a history entry for a URL whose content was never actually applied to the DOM.
      if (/** @type {Error} */ (error).name !== 'AbortError') throw error;
    } finally {
      endSoftNav(thisController);
      if (this.#currentFetchController === thisController) this.#currentFetchController = null;
    }
  }

  /**
   * Core DOM update: fetches sections and applies them to the page via the
   * shared soft-nav section-swap module (also used by soft-navigation.js for
   * length/gear-set navigation), passing the prefetch cache through so a
   * hover-warmed navigation reuses it instead of re-fetching.
   * Used by both #softNavigate (click) and #handlePopstate (back/forward).
   * Does NOT modify history — callers are responsible for that.
   * Rethrows AbortError (rather than swallowing it) so callers can tell a superseded navigation
   * apart from a genuinely-applied one, instead of independently re-checking `signal.aborted`.
   * @param {string} url - The product URL to fetch.
   * @param {AbortSignal} [signal] - Optional signal to cancel the fetch.
   * @param {object} [options]
   * @param {boolean} [options.wholePage] - Swap the whole template area instead of the colourway sections.
   */
  async #updatePageContent(url, signal, { wholePage = false } = {}) {
    this.#isLoading = true;
    this.#setLoadingState(true);

    try {
      if (wholePage) {
        await swapSoftNavPage(url, { signal });
      } else {
        await swapSoftNavSections(url, {
          signal,
          fetchSections: (fetchUrl, fetchSignal) => prefetchManager.get(fetchUrl, fetchSignal),
        });
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') throw error;
      window.location.href = url;
    } finally {
      this.#isLoading = false;
      this.#setLoadingState(false);
    }
  }

  /**
   * Sets loading state on the component.
   * @param {boolean} loading - Whether loading is in progress.
   */
  #setLoadingState(loading) {
    if (this.#loadingTimer) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = null;
    }

    if (loading) {
      this.#loadingTimer = setTimeout(() => {
        this.#loadingTimer = null;
        this.classList.add('is-loading');
        const productInfo = document.querySelector('#shopify-section-main');
        if (productInfo) {
          productInfo.classList.add('is-loading');
          productInfo.setAttribute('aria-busy', 'true');
        }
      }, 400);
    } else {
      this.classList.remove('is-loading');
      const productInfo = document.querySelector('#shopify-section-main');
      if (productInfo) {
        productInfo.classList.remove('is-loading');
        productInfo.setAttribute('aria-busy', 'false');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#loadingTimer) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = null;
    }

    if (this.#popstateHandler) {
      window.removeEventListener('popstate', this.#popstateHandler);
      this.#popstateHandler = null;
      const w = /** @type {any} */ (window);
      w.__productVariationsPopstateOwner = Math.max(0, (w.__productVariationsPopstateOwner || 0) - 1);
      const ownerIndex = popstateOwners.indexOf(this);
      if (ownerIndex !== -1) popstateOwners.splice(ownerIndex, 1);
    }

    // Don't abort #currentFetchController here: this callback most often fires as a side
    // effect of the very swap that controller is driving (replacing #shopify-section-main
    // removes this element mid-navigation), so self-aborting would turn an already-applied
    // update into a discarded one and skip history.pushState/scroll-restore in the caller.
    // Real cross-navigation cancellation is centralized via beginSoftNav/endSoftNav, and
    // #softNavigate/#handlePopstate's own `finally` blocks release the controller once their
    // promise settles regardless of connection state, so nothing here needs to abort it.
    this.#currentFetchController = null;

    this.#unbindCardHover?.();
    this.#unbindCardHover = null;
  }
}

if (!customElements.get('product-variations')) {
  customElements.define('product-variations', ProductVariations);
}
